import React, { useRef } from "react";
import { Box, Button, IconButton, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import CloseIcon from "@mui/icons-material/Close";

const ImageUploader = ({
  label,
  maxSizeMB = 15,
  allowedTypes = ["image/jpeg", "image/png", "image/jpg", "image/webp"],
  onError,
  imageUrl,
  setImageUrl,
  tempImageFile,
  setTempImageFile,
  avatarImgUrl = "",
  showRemoveButton = true,
  isLoading = false,
  buttonSx = {},
  imageStyle = {},
  labelSx = {},
  hint,
  buttonAlignment = "column",
  hintAlignment = "column",
  onImageDelete,
  initialImageUrl,
  resetImageDeleted,
  removeButtonSx = {},
  hintAlignmentBoxSx = {},
}) => {
  const fileInputRef = useRef();

  const { t } = useTranslation("components");

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!allowedTypes.includes(file.type)) {
      onError?.(t("invalid_file_type"));
      return;
    }

    const maxSize = maxSizeMB * 1024 * 1024;
    if (file.size > maxSize) {
      onError?.(t("file_too_large", { size: maxSizeMB }));
      return;
    }

    setTempImageFile(file);
    resetImageDeleted?.();
  };

  const handleRemoveImage = () => {
    setTempImageFile(null);
    setImageUrl?.(null);
    initialImageUrl && onImageDelete?.();
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: buttonAlignment, gap: "12px" }}>
      {label && (
        <Typography
          color="rgba(47, 43, 61, 0.9)"
          sx={{ fontSize: "15px", fontWeight: 500, ...labelSx }}
        >
          {label}
        </Typography>
      )}

      {(imageUrl || tempImageFile || avatarImgUrl) && (
        <Box
          sx={{
            position: "relative",
            width: 110,
            height: 110,
            borderRadius: "50%",
            ...imageStyle,
          }}
        >
          <img
            src={
              tempImageFile
                ? URL.createObjectURL(tempImageFile)
                : imageUrl
                  ? imageUrl
                  : avatarImgUrl
            }
            alt="preview"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              borderRadius: "50%",
              ...imageStyle,
            }}
          />
          {showRemoveButton && (
            <IconButton
              onClick={handleRemoveImage}
              size="small"
              disabled={isLoading}
              sx={{
                position: "absolute",
                top: "0px",
                right: "-12px",
                backgroundColor: "rgba(0, 0, 0, 0.4)",
                color: "rgba(255, 255, 255, 0.9)",
                zIndex: 2,
                padding: "2px",
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.6)",
                },
                ...removeButtonSx,
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      )}

      <Box
        sx={{
          display: "flex",
          gap: "12px",
          flexDirection: hintAlignment,
          justifyContent: "center",
          alignItems: "flex-start",
          maxWidth: "100%",
          ...hintAlignmentBoxSx,
        }}
      >
        <Button
          variant="contained"
          component="label"
          sx={{
            width: "max-content",
            alignSelf: "flex-start",
            textTransform: "none",
            color: "rgba(36, 139, 112, 1)",
            fontSize: "13px",
            borderRadius: "8px",
            backgroundColor: isLoading
              ? "rgba(36, 139, 112, 0.05)"
              : "rgba(242, 243, 251, 1)",
            boxShadow: "none",
            "&:hover": {
              backgroundColor: isLoading
                ? "rgba(36, 139, 112, 0.05)"
                : "rgba(36, 139, 112, 0.2)",
              boxShadow: "none",
            },
            ...buttonSx,
          }}
          disabled={isLoading}
        >
          {imageUrl
            ? t("ImageUploader_change_button_text")
            : t("ImageUploader_upload_button_text")}
          <input
            type="file"
            hidden
            accept={allowedTypes.join(",")}
            onChange={handleFileChange}
            ref={fileInputRef}
            disabled={isLoading}
          />
        </Button>

        {hint && (
          <Typography
            sx={{
              fontSize: "13px",
              fontWeight: 400,
              color: "rgba(47, 43, 61, 0.9)",
            }}
          >
            {hint}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default ImageUploader;
